import { Modal, Form, Select, Input, notification } from 'antd'
import { FC, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { BlockTrainStatusEnum, BlocktrainType } from '@types'

import useUpdateBlocktrainStatus from '../../../hooks/blocktrain/useUpdateBlocktrainStatus'

interface Props {
  visible: boolean
  close: () => void
  blocktrain: BlocktrainType | null
}

const ChangeBlocktrainStatusModal: FC<Props> = ({ visible, close, blocktrain }) => {
  const { t } = useTranslation()
  const [form] = Form.useForm()
  const { updateBlocktrainStatus, updateBlocktrainStatusIsFetching } = useUpdateBlocktrainStatus()

  const [selectedStatus, setSelectedStatus] = useState<BlockTrainStatusEnum | undefined>()

  useEffect(() => {
    if (blocktrain) {
      form.setFieldsValue({
        currentStatus: t(`blocktrain_status.${blocktrain.status}`),
        newStatus: undefined,
        railCarWayBill: undefined,
      })
      setSelectedStatus(undefined)
    }
  }, [blocktrain, form, t])

  const onFinish = async () => {
    const { newStatus, railCarWayBill } = await form.validateFields()
    if (!blocktrain) return

    await updateBlocktrainStatus({
      id: String(blocktrain.id),
      status: newStatus,
      ...(selectedStatus &&
        [
          BlockTrainStatusEnum.TRANSIT,
          BlockTrainStatusEnum.UNLOADING,
          BlockTrainStatusEnum.FINISHED,
        ].includes(selectedStatus) && {
          railCarWayBill,
        }),
    })

    notification.success({
      message: t('status_updated_success'),
    })
    close()
  }

  const shouldShowWaybill = [
    BlockTrainStatusEnum.TRANSIT,
    BlockTrainStatusEnum.UNLOADING,
    BlockTrainStatusEnum.FINISHED,
  ].includes(selectedStatus ?? BlockTrainStatusEnum.LOADING)

  return (
    <Modal
      title={t('blocktrain_change_status')}
      open={visible}
      onCancel={close}
      onOk={onFinish}
      okButtonProps={{ loading: updateBlocktrainStatusIsFetching }}
      cancelText={t('cancel')}
      okText={t('confirm')}
      className="entity-modal"
    >
      <Form form={form} layout="horizontal">
        <span className="subtitle-modal-wrap">
          {blocktrain
            ? `${t('blockTrain_Info')}: #${blocktrain.id ?? '-'} - ${
                blocktrain.supplier?.tradeName ?? blocktrain.tradeName ?? '-'
              } - ${
                typeof blocktrain.origin === 'object' &&
                blocktrain.origin !== null &&
                typeof (blocktrain.origin as { city?: unknown }).city === 'string'
                  ? (blocktrain.origin as { city: string }).city
                  : typeof blocktrain.origin === 'string'
                  ? blocktrain.origin
                  : '-'
              } - ${blocktrain.railCarQuantity ?? '-'}`
            : ''}
        </span>
        <Form.Item label={t('current_status')} name="currentStatus">
          <Input disabled />
        </Form.Item>
        <Form.Item
          label={t('new_status')}
          name="newStatus"
          rules={[{ required: true, message: t('please_select_new_status') }]}
        >
          <Select
            placeholder={t('select_new_status')}
            onChange={value => {
              setSelectedStatus(value)
              if (
                ![
                  BlockTrainStatusEnum.TRANSIT,
                  BlockTrainStatusEnum.UNLOADING,
                  BlockTrainStatusEnum.FINISHED,
                ].includes(value)
              ) {
                form.setFieldsValue({ railCarWayBill: undefined })
              }
            }}
          >
            {Object.values(BlockTrainStatusEnum)
              .filter(
                status => status !== blocktrain?.status && status !== BlockTrainStatusEnum.LOADING,
              )
              .map(status => (
                <Select.Option key={status} value={status}>
                  {t(`blocktrain_status.${status}`)}
                </Select.Option>
              ))}
          </Select>
        </Form.Item>

        {shouldShowWaybill && (
          <Form.Item label={t('railway_bill_#')} name="railCarWayBill">
            <Input placeholder={t('enter_waybill')} />
          </Form.Item>
        )}
      </Form>
    </Modal>
  )
}

export default ChangeBlocktrainStatusModal
